import React, { useState, useEffect, useRef } from "react";

import { DataGrid } from "@material-ui/data-grid";
import { Container, Grid } from "@material-ui/core";

import { GridStyled } from "../../styles/GlobalStyle";
import MaskService from "../../utils/mask/maskService";
import { TitleTable } from "../../components/basic/TitleTable";
import { getMapsWithoutContract } from "../../repositories/v1/map";
import { GraphicDoughnut } from "./children/GraphicDoughnut";
import { GraphicLine } from "./children/GraphicLine";
import {
  reportContractProduct,
  reportMapProduct,
  reportMapProductMonths,
} from "../../repositories/v1/report";
import ExportExcel from "./children/ExportExcel";

function ReportContract() {
  const [page, setPage] = useState(0);
  const [rowsFilter, setRowsFilter] = useState([]);
  const [loadings, setLoadings] = useState({});
  const [dataGraphicContract, setDataGraphicContract] = useState({});
  const [dataGraphicMap, setDataGraphicMap] = useState({});
  const [dataGraphicMonth, setDataGraphicMonth] = useState({});
  const searchRef = useRef(null);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsToExport, setRowsToExport] = useState([]);
  const [contractTextFilter, setContractTextFilter] = useState("");

  const columns = [
    {
      field: "ClienteCNPJ",
      headerName: "CNPJ/CPF",
      width: 150,
      renderCell: ({ row }) =>
        MaskService.toMask(
          row?.ClienteCNPJ.length > 11 ? "cnpj" : "cpf",
          row?.ClienteCNPJ || ""
        ),
    },
    {
      field: "Fantasia",
      headerName: "Fantasia",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "Produto",
      headerName: "Produto",
      minWidth: 240,
      flex: 1,
    },
    {
      field: "Versao",
      headerName: "Versão",
      minWidth: 240,
      flex: 1,
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        setLoadings((current) => ({ ...current, contractGraphic: true }));

        const { data: response } = await reportContractProduct();

        setDataGraphicContract(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, contractGraphic: false }));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadings((current) => ({ ...current, mapGraphic: true }));

        const { data: response } = await reportMapProduct();

        setDataGraphicMap(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, mapGraphic: false }));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadings((current) => ({ ...current, monthGraphic: true }));

        const { data: response } = await reportMapProductMonths({
          qtdMeses: 12,
        });

        setDataGraphicMonth(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, monthGraphic: false }));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoadings((current) => ({ ...current, table: true }));

        const {
          data: { data: callback },
        } = await getMapsWithoutContract({
          pageNumber: page,
          queryString: contractTextFilter,
        });

        setRowsFilter(callback?.maps);
        setTotalRows(callback?.count);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, table: false }));
      }
    })();
  }, [contractTextFilter, page]);

  useEffect(() => {
    (async () => {
      try {
        setLoadings((current) => ({ ...current, exportButton: true }));

        const {
          data: { data: callback },
        } = await getMapsWithoutContract({
          queryString: contractTextFilter,
        });

        setRowsToExport(callback?.maps);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadings((current) => ({ ...current, exportButton: false }));
      }
    })();
  }, [contractTextFilter]);

  const handleSearch = (e) => {
    e.preventDefault();

    setContractTextFilter(searchRef?.current?.value);
    setPage(0);
  };

  return (
    <Container maxWidth="lg">
      <GridStyled
        container
        padding="20px 0 20px 0"
        justify="center"
        spacing={3}
      >
        <GridStyled
          item
          container
          justify="center"
          alignItems="center"
          spacing={5}
        >
          <Grid item md={4} sm={6} xs={12}>
            <GraphicDoughnut
              loading={loadings.contractGraphic}
              title="Itens dos Contratos por Produto"
              data={dataGraphicContract}
            />
          </Grid>

          <Grid item md={4} sm={6} xs={12}>
            <GraphicDoughnut
              loading={loadings.contractGraphic}
              title="Mapas por Produto"
              data={dataGraphicMap}
            />
          </Grid>

          <Grid item md={8} xs={12}>
            <GraphicLine
              loading={loadings.monthGraphic}
              title="Itens dos Contratos X Mapas (por período)"
              data={dataGraphicMonth}
            />
          </Grid>
        </GridStyled>

        <Grid item xs={12}>
          <TitleTable
            inputRef={searchRef}
            labelFilter="Filtro (CNPJ, Razão, Fantasia, Produto, Versão)"
            title={
              <Grid container alignItems="center" justify="center" spacing={1}>
                <Grid item flex={1}>
                  Mapas sem Contrato
                </Grid>
                {!loadings.exportButton && (
                  <Grid item flex={1}>
                    <ExportExcel
                      disabled={loadings.exportButton}
                      rowsToExport={rowsToExport}
                    />
                  </Grid>
                )}
              </Grid>
            }
            loading={!!loadings.table}
            handleFilter={handleSearch}
          />
        </Grid>

        <Grid item xs={12}>
          <DataGrid
            autoHeight
            disableColumnMenu
            pageSize={10}
            getRowId={(row) => row.Id}
            isRowSelectable={false}
            disableColumnSelector={true}
            disableSelectionOnClick={true}
            disableDensitySelector={true}
            disableColumnReorder={true}
            loading={!!loadings.table}
            rows={loadings.table ? [] : rowsFilter}
            columns={columns}
            page={page}
            onPageChange={(e) => setPage(e?.page || 0)}
            rowCount={totalRows}
            pagination
            paginationMode={"server"}
          />
        </Grid>
      </GridStyled>
    </Container>
  );
}

export { ReportContract };
